<script lang="ts">
	import { Device } from "luxedo-data"
	import { type Unsubscriber } from "svelte/store"
	import { onDestroy } from "svelte"
	import { openCalibrationOverlay } from "../../../../reusable/overlays/calibration"
	import ExpandIcon from "../../../../reusable/icons/ExpandIcon.svelte"
	import { Responsive } from "responsive"
	import { ProgressBar } from "misc-ui"
	import ProjectorIcon from "../../../../reusable/icons/ProjectorIcon.svelte"
	import { hideMinimizedCalibration } from "../../../../reusable/overlays/calibration/calibration-minimized"
	import { CalibrationMinimizedController } from "../../../../reusable/overlays/calibration/calibration-minimized/CalibrationMinimizedController.svelte"
	import { CalibrationController } from "../../../../reusable/overlays/calibration/CalibrationController.svelte"

	interface Props {
		device: Device
	}

	let { device }: Props = $props()

	let isOpen = $derived(
		device.id in CalibrationMinimizedController.instances &&
			CalibrationMinimizedController.instances[device.id]
	)
	let calRun = $derived(
		device.id in CalibrationController.runs ? CalibrationController.runs[device.id] : undefined
	)

	let message: string = $derived(calRun?.progress?.message)
	let progress: number = $derived(calRun?.progress?.progress)
	let isFailed: boolean = $derived(calRun?.progress?.didError)
	let isComplete: boolean = $derived(!!calRun?.snapshot)

	function onClick() {
		openCalibrationOverlay(device)
		hideMinimizedCalibration(device)
	}
</script>

{#if isOpen}
	<div id="calibration-minimized" onclick={onClick}>
		<div class="minimized-calibration-heading">
			<span>Calibrating {device.name}...</span>
			<button class="icon small">
				<ExpandIcon />
			</button>
		</div>
		<div class="flex-row">
			<div class="left">
				<ProjectorIcon {device} />
			</div>
			<div class="right">
				<div class="calibration-message">
					{#if isFailed}
						<span class="error">
							Calibration failed... {Responsive.isMobile ? "Tap" : "Click"} to troubleshoot.
						</span>
					{:else if isComplete}
						Calibration complete! {Responsive.isMobile ? "Tap" : "Click"} to view the generated snapshot.
					{:else}
						{message}
					{/if}
				</div>
			</div>
		</div>
		<ProgressBar percentage={isComplete ? 1 : progress} />
	</div>
{/if}

<style>
	.minimized-calibration-heading {
		font-size: var(--h3);
		color: var(--color-text);
		margin-bottom: 0.5rem;
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
	}

	#calibration-minimized {
		background-color: var(--color-bg-darker);
		border-radius: var(--br);
		box-shadow: var(--shadow-medium);
		padding: 0.5rem 1rem;

		cursor: pointer;
	}

	:global(.isMobile) #calibration-minimized {
		background-color: var(--color-bg);
	}

	#calibration-minimized :global(.progress-bar) {
		margin: 0.5rem 0;
	}

	.calibration-message {
		line-height: 1em;
		margin-bottom: 0.75rem;
	}

	.flex-row {
		align-items: center;
	}

	.flex-row .right {
		margin-left: 0.5rem;
	}

	.left :global(.projector-icon) {
		width: 2rem;
		height: auto;
	}

	.error {
		color: var(--color-error);
	}

	button.icon.small {
		padding: 0;
	}

	button.icon.small :global(svg) {
		height: 1.25rem;
	}

	button.icon.small :global(.svg-stroke) {
		stroke: var(--color-text);
	}

	button.icon.small:focus-visible :global(.svg-stroke),
	button.icon.small:hover :global(.svg-stroke) {
		stroke: var(--color-text-light);
	}
</style>
